import React, { useEffect, useState } from 'react';
import Price from '../../Price';
import Money from '../../Money';
import LoadingIcon from './Loading';
import { getSizedImageUrl } from '../../../../scripts/utils/image';
import { LineItem } from '../../../util/typings';
import useCartLineItem from '../../../hooks/useCartLineItem';
import { TrashIcon } from './TrashIcon';
import { useCart } from '../../../hooks/useCart';
import {
  getKgOriginalUnitPrice,
  getKgSavingsLineAmount,
  isKgProductLineItem,
} from '../../../util/cartKgSavings';

type Props = {
  lineItem: LineItem;
};

function getSellingPlans(lineItem: LineItem) {
  return fetch(`https://${window.Shopify.shop}/api/2025-07/graphql.json`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      query: `
        {
          product(handle: "${lineItem.handle}") {
            sellingPlanGroups(first: 100) {
              nodes {
                sellingPlans(first: 100) {
                  nodes {
                    name
                    id
                    priceAdjustments {
                      ... on SellingPlanPriceAdjustment {
                        adjustmentValue {
                          ... on SellingPlanPercentagePriceAdjustment {
                            adjustmentPercentage
                          }
                        }
                      }
                    }
                    checkoutCharge {
                      value {
                        __typename
                        ... on MoneyV2 {
                          amount
                        }
                        ... on SellingPlanCheckoutChargePercentageValue {
                          percentage
                        }
                      } 
                    }
                  }
                }
              }
            }
          }
        }
      `,
    }),
  })
    .then((response) => response.json())
    .then(({ data }) =>
      data.product?.sellingPlanGroups?.nodes.flatMap(
        (group) => group.sellingPlans.nodes,
      ),
    )
    .catch((error) => {
      throw error;
    });
}

function parseFrequency(sellingPlan: { name: string }): number {
  return parseInt(sellingPlan.name.split(' ').find((x) => !isNaN(parseInt(x))));
}

const CartLineItem: React.FunctionComponent<Props> = ({ lineItem }: Props) => {
  const {
    increment,
    changeQuantity,
    decrement,
    remove,
    removing,
    itemUpdating,
    displayQuantity,
  } = useCartLineItem(lineItem);

  const { updateItem } = useCart();

  const hasSellingPlan = Boolean(lineItem.selling_plan_allocation);
  const isKgProduct = isKgProductLineItem(lineItem);
  const kgOriginalUnitPrice = getKgOriginalUnitPrice(lineItem);
  const kgSavingsLineAmount = getKgSavingsLineAmount(lineItem);

  const [sellingPlans, setSellingPlans] = useState(null);
  useEffect(() => {
    getSellingPlans(lineItem).then((sellingPlans) => {
      const sortedSellingPlans = sellingPlans.sort((a, b) => {
        return parseFrequency(a) - parseFrequency(b);
      });
      setSellingPlans(sortedSellingPlans);
    });
  }, [lineItem]);

  function parseSellingPlanId(id: string) {
    return +id.split('/').pop();
  }

  function changeSellingPlan(e: React.ChangeEvent<HTMLSelectElement>) {
    if (e.target.value === 'one-time-purchase') {
      updateItem(lineItem.key, {
        sellingPlan: null,
        properties: lineItem.properties,
        quantity: lineItem.quantity,
      });
      return;
    }
    updateItem(lineItem.key, {
      sellingPlan: +e.target.value,
      properties: lineItem.properties,
      quantity: lineItem.quantity,
    });
  }

  function upgradeToSub() {
    const defaultSellingPlan =
      sellingPlans.find((sellingPlan) => parseFrequency(sellingPlan) === 4) ||
      sellingPlans[0];
    updateItem(lineItem.key, {
      sellingPlan: parseSellingPlanId(defaultSellingPlan.id),
      properties: lineItem.properties,
      quantity: lineItem.quantity,
    });
  }

  const renderImage = (lineItem: LineItem) => {
    return lineItem.featured_image.url ? (
      <img
        alt={lineItem.product_title}
        loading="lazy"
        width="110"
        height="100"
        src={
          lineItem.featured_image.url
            ? getSizedImageUrl(lineItem.featured_image.url, '220x220')
            : ''
        }
      ></img>
    ) : (
      ''
    );
  };

  function getAdjustedPrice(price: number) {
    const adjustmentPercentage = sellingPlans?.at(0)?.priceAdjustments?.at(0)
      ?.adjustmentValue?.adjustmentPercentage;
    if (!adjustmentPercentage) {
      return price;
    }
    return Math.round((price * (100 - adjustmentPercentage)) / 100);
  }

  return (
    <li className={`minicart-item ${removing ? 'removing' : ''}`}>
      <a
        className="minicart-item-image-wrap"
        href={`/products/${lineItem.handle}`}
      >
        {renderImage(lineItem)}
      </a>
      <div className="minicart-item-details">
        <a
          href={`/products/${lineItem.handle}`}
          className="minicart-item-title"
        >
          {lineItem.product_title}
        </a>
        {lineItem.variant_title ? (
          <div className="minicart-item-variant-title">
            {lineItem.variant_title}
          </div>
        ) : null}
        <div className="minicart-item-price-quantity-container">
          <div className="minicart-items-quantity">
            <button
              aria-label={`Decrease quantity of ${lineItem.product_title}`}
              disabled={itemUpdating}
              onClick={decrement}
            >
              -
            </button>
            <span className="minicart-item-quantity-number">
              {itemUpdating ? <LoadingIcon /> : ''}
              <input
                type="number"
                aria-label={`${lineItem.product_title} quantity: ${
                  displayQuantity
                }`}
                value={displayQuantity}
                onBlur={(e) =>
                  e.target.value ? null : changeQuantity(lineItem.quantity)
                } //if blank when blured switch back to actual value
                onChange={(e) => changeQuantity(e.target.value)}
              ></input>
            </span>
            <button
              aria-label={`Increase quantity of ${lineItem.product_title}`}
              disabled={
                itemUpdating ||
                !lineItem.availablity_remaining
              }
              onClick={increment}
            >
              +
            </button>
          </div>
          <button
            aria-label={`Remove ${lineItem.product_title} from cart`}
            className="minicart-item-remove"
            onClick={remove}
          >
            <TrashIcon />
          </button>
          <Price
            originalPrice={isKgProduct ? kgOriginalUnitPrice : lineItem.price}
            finalPrice={lineItem.price}
            priceVaries={false}
            withCurrency={false}
          />
        </div>
        {isKgProduct && kgSavingsLineAmount > 0 && (
          <div className="minicart-item-discount">
            You save <Money amount={kgSavingsLineAmount} /> (15%)
          </div>
        )}
        <div className="minicart-item-delivery-info">
          {hasSellingPlan ? (
            <>
              {sellingPlans && sellingPlans.length > 1 ? (
                <select
                  onChange={changeSellingPlan}
                  value={lineItem.selling_plan_allocation?.selling_plan?.id}
                >
                  <optgroup label="Pay Full Price">
                    <option value="one-time-purchase">One-time only</option>
                  </optgroup>
                  <optgroup label="Subscribe and Save 10%">
                    {sellingPlans?.map((group) => (
                      <option
                        key={group.id}
                        value={parseSellingPlanId(group.id)}
                      >
                        {group.name}
                      </option>
                    ))}
                  </optgroup>
                </select>
              ) : (
                <div className="minicart-item-selling-plan--single">
                  {lineItem.selling_plan_allocation.selling_plan.name}
                </div>
              )}
            </>
          ) : (
            <>
              {sellingPlans && sellingPlans.length > 1 ? (
                <div className="minicart-item-selling-plan--upgrade-to-sub">
                  <button
                    className="minicart-item-selling-plan--upgrade-to-sub-button"
                    onClick={upgradeToSub}
                    aria-label="Upgrade to subscription"
                  ></button>
                  <header className="minicart-item-selling-plan--upgrade-to-sub-header">
                    <span>Subscribe + Save 10%</span>
                    <span>
                      <Price
                        originalPrice={getAdjustedPrice(lineItem.price)}
                        finalPrice={getAdjustedPrice(lineItem.price)}
                        priceVaries={false}
                        withCurrency={false}
                      />
                    </span>
                  </header>
                  <div className="minicart-item-selling-plan--upgrade-to-sub-footer">
                    <span>Edit, skip or cancel anytime</span>
                    <span>
                      <s>
                        <Price
                          originalPrice={lineItem.price}
                          finalPrice={lineItem.price}
                          priceVaries={false}
                          withCurrency={false}
                        />
                      </s>
                    </span>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </li>
  );
};

export default CartLineItem;
